import React from 'react';
import { ArrowRight, Users, Globe } from 'lucide-react';
import { useData } from '../context/DataContext';

const HeroSection = ({ setActiveTab }) => {
  const { projectInfo, loading } = useData();

  if (loading || !projectInfo) {
    return (
      <section className="relative min-h-[calc(100vh-80px)] flex items-center justify-center bg-gradient-to-br from-[#1e3a5f] via-[#2c5282] to-[#1e3a5f]">
        <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full"></div>
      </section>
    );
  }

  return (
    <section className="relative min-h-[calc(100vh-80px)] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#1e3a5f] via-[#2c5282] to-[#1e3a5f]">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#4299e1]/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Grid Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-white/20 rounded-full blur-xl scale-150" />
            <img
              src={projectInfo.logo}
              alt="Project Logo"
              className="relative h-40 w-40 sm:h-48 sm:w-48 object-contain drop-shadow-2xl"
            />
          </div>
        </div>

        {/* Erasmus+ Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
          <Globe className="w-4 h-4 text-[#90cdf4]" />
          <span className="text-white/90 text-sm font-medium">Erasmus+ KA2 Partnership</span>
        </div>

        {/* Title */}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
          {projectInfo.name}
        </h1>

        {/* Tagline */}
        <p className="text-xl sm:text-2xl lg:text-3xl text-[#90cdf4] font-light mb-8 italic">
          "{projectInfo.tagline}"
        </p>

        {/* Description */}
        <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
          A collaborative project between Sweden and Türkiye, developing innovative
          approaches to conflict resolution in educational settings.
        </p>

        {/* Partner Countries */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <div className="flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
            <span className="text-2xl">🇸🇪</span>
            <span className="text-white font-medium">Sweden</span>
          </div>
          <div className="flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
            <span className="text-2xl">🇹🇷</span>
            <span className="text-white font-medium">Türkiye</span>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={() => setActiveTab('about')}
          className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1e3a5f] rounded-xl font-semibold shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
        >
          <Users className="w-5 h-5" />
          Discover Our Project
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </div>

      {/* EU Funding Note */}
      <div className="absolute bottom-4 right-4 text-white/50 text-xs">
        Co-funded by the European Union
      </div>
    </section>
  );
};

export default HeroSection;
